import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        // LG vendor edit (owner-approved 2026-09-08; horizon-mechanics §4.47):
        // a Recharge dynamic bundle counts as ONE item, so the SR announcement
        // must not read the raw `cart.totalQuantity`. Prefer the corrected count
        // recomputed from the resolved `detail.items` (raw `_rc_bundle`
        // properties: every non-bundle qty + 1 per distinct bundle); fall back
        // to `totalQuantity` when detail is absent.
        // Scope confirmed 2026-09-10 (vs the plan's "one line, read the carrier"):
        // the carrier lives in detail.sections HTML — reading it needs the same
        // ~10 lines plus DOM parsing, and a shared helper would couple this
        // VENDORED file to an LG module (§5.12 upgrade cost). Event-time twin of
        // lg-cart-bundle.js's fallback recompute; the authoritative rule is
        // snippets/lg-cart-display-count.liquid (§4.47) — keep the three in sync.
        let cartCount = cart?.totalQuantity;
        const items = detail?.items;
        if (Array.isArray(items)) {
          const seen = new Set();
          let corrected = 0;
          for (const item of items) {
            const bundle = item?.properties?._rc_bundle;
            if (bundle) {
              if (!seen.has(bundle)) {
                seen.add(bundle);
                corrected += 1;
              }
            } else {
              corrected += item?.quantity ?? 0;
            }
          }
          cartCount = corrected;
        }
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
